/**
 * DatePeriodFilter — filtro de período EXPLÍCITO y reutilizable para Reportes.
 *
 * Reemplaza los botones ambiguos Semana/Mes/Trimestre/Año (que no decían QUÉ
 * período veías). Aquí el período activo SIEMPRE es legible ("Junio 2026",
 * "Q2 2026", "2026") y se elige Año + Mes explícitos.
 *
 * Pensado para REUSO: Reportes de CRM hoy, y las futuras pestañas de Reportes de
 * Boutique/GastroGest (mismo requerimiento en todos los módulos). No depende de
 * ningún dato de CRM — solo emite el período elegido.
 *
 * Mapea al backend GET /timelogs/summary que acepta `period` + `year`/`month`
 * (ancla explícita). Rango personalizado (from/to) = follow-up documentado.
 */
import { useMemo } from 'react';

export type PeriodMode = 'month' | 'quarter' | 'year';

export interface PeriodValue {
  mode: PeriodMode;
  year: number;
  /** 1-12. Relevante en mode 'month' y 'quarter'. */
  month: number;
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/** Período activo en texto legible: "Junio 2026" · "Q2 2026" · "2026". */
export function periodLabel(v: PeriodValue): string {
  if (v.mode === 'year') return `${v.year}`;
  if (v.mode === 'quarter') return `Q${Math.floor((v.month - 1) / 3) + 1} ${v.year}`;
  return `${MONTHS[v.month - 1]} ${v.year}`;
}

/** Convierte el período a los query params del endpoint de summary. */
export function periodToQuery(v: PeriodValue): Record<string, string> {
  if (v.mode === 'year') return { period: 'year', year: String(v.year) };
  if (v.mode === 'quarter') return { period: 'quarter', year: String(v.year), month: String(v.month) };
  return { period: 'month', year: String(v.year), month: String(v.month) };
}

/** Valor inicial por defecto: mes en curso. */
export function defaultPeriod(): PeriodValue {
  const now = new Date();
  return { mode: 'month', year: now.getFullYear(), month: now.getMonth() + 1 };
}

const MODE_LABEL: Record<PeriodMode, string> = { month: 'Mes', quarter: 'Trimestre', year: 'Año' };

export function DatePeriodFilter({
  value,
  onChange,
  yearsBack = 2,
  yearsForward = 1,
}: {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
  yearsBack?: number;
  yearsForward?: number;
}) {
  const years = useMemo(() => {
    const cur = new Date().getFullYear();
    const arr: number[] = [];
    for (let y = cur - yearsBack; y <= cur + yearsForward; y += 1) arr.push(y);
    // Asegura que el año seleccionado esté en la lista aunque caiga fuera del rango.
    if (!arr.includes(value.year)) arr.push(value.year);
    return arr.sort((a, b) => b - a);
  }, [value.year, yearsBack, yearsForward]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Modo: Mes / Trimestre / Año */}
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
        {(['month', 'quarter', 'year'] as PeriodMode[]).map((m) => (
          <button key={m} onClick={() => onChange({ ...value, mode: m })}
            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${value.mode === m ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>

      {/* Mes (oculto en modo Año) */}
      {value.mode !== 'year' && (
        <select className="input !w-auto !py-1.5 text-sm" value={value.month}
          onChange={(e) => onChange({ ...value, month: Number(e.target.value) })}>
          {MONTHS.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
        </select>
      )}

      {/* Año */}
      <select className="input !w-auto !py-1.5 text-sm" value={value.year}
        onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>

      {/* Período activo, siempre legible */}
      <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
        {periodLabel(value)}
      </span>
    </div>
  );
}
